import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { Control, useForm } from 'react-hook-form';

import {
  AssetDefaultOrSelectedTypeMap,
  PublicOfferAssetToInvestNormalized,
  PublicOfferDetailsModalityWithTimelineNormalized,
} from '../../../../../../../store/product/types';

import {
  PublicOfferFormDataT3,
  getEquityValueDescription,
  getValidationCreateSchemaT3,
} from './constants';

export const usePublicOfferAssetEquityController = (
  offer: AssetDefaultOrSelectedTypeMap['publicOffers'],
  _: PublicOfferAssetToInvestNormalized,
  modality: PublicOfferDetailsModalityWithTimelineNormalized
) => {
  const minimumValue = modality.minimumApplication;
  const maximumValue = modality.maximumApplication;

  const minimumEquityValue = offer.minimumEquityValue;
  const maximumEquityValue = offer.maximumEquityValue;

  const [isValueFocused, setIsValueFocused] = useState(false);
  const [isEquityValueFocused, setIsEquityValueFocused] = useState(false);
  const [valueApplied, setValueApplied] = useState(minimumValue);
  const [equityValueApplied, setEquityValueApplied] = useState(minimumEquityValue);

  const valueAppliedFormatted = Utils.Mask.Money(valueApplied);
  const minValueFormatted = Utils.Mask.Money(minimumValue);
  const minEquityValueFormatted = Utils.Mask.Money(minimumEquityValue);
  const equityValueAppliedFormatted = Utils.Mask.Money(equityValueApplied);

  const tableForm = useForm<PublicOfferFormDataT3>({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: {
      priceItem: minValueFormatted,
      equityValue: minEquityValueFormatted,
    },
    resolver: zodResolver(
      getValidationCreateSchemaT3(
        minimumValue,
        maximumValue,
        minimumEquityValue,
        maximumEquityValue
      )
    ),
  });

  const handleInputValueFocus = () => {
    setIsValueFocused(true);
    if (tableForm.formState.errors?.priceItem) {
      tableForm.resetField('priceItem');
    }
  };

  const handleInputEquityValueFocus = () => {
    setIsEquityValueFocused(true);
    if (tableForm.formState.errors?.equityValue) {
      tableForm.resetField('equityValue');
    }
  };

  const handleInputValueClear = () => {
    setIsValueFocused(false);
    tableForm.resetField('priceItem');
    tableForm.clearErrors('priceItem');
  };

  const handleInputEquityValueClear = () => {
    setIsEquityValueFocused(false);
    tableForm.resetField('equityValue');
    tableForm.clearErrors('equityValue');
  };

  const handleInputValueBlur = async () => {
    const currentValue = tableForm.getValues('priceItem');
    const currentValueUnformatted = Utils.Unmask.Money(currentValue);

    if (currentValueUnformatted >= minimumValue && currentValueUnformatted <= maximumValue) {
      tableForm.setValue('priceItem', currentValue);
      setValueApplied(currentValueUnformatted);
    } else {
      tableForm.clearErrors('priceItem');
      tableForm.setValue('priceItem', Utils.Mask.Money(minimumValue));
      setValueApplied(minimumValue);
    }

    if (isValueFocused) {
      setIsValueFocused(false);
    }
  };

  const handleInputEquityValueBlur = async () => {
    const currentEquityValue = tableForm.getValues('equityValue');
    const currentEquityValueUnformatted = Utils.Unmask.Money(currentEquityValue);

    if (
      currentEquityValueUnformatted >= minimumEquityValue &&
      currentEquityValueUnformatted <= maximumEquityValue
    ) {
      tableForm.setValue('equityValue', currentEquityValue);
      setEquityValueApplied(currentEquityValueUnformatted);
    } else {
      tableForm.clearErrors('equityValue');
      tableForm.setValue('equityValue', Utils.Mask.Money(minimumEquityValue));
      setEquityValueApplied(minimumEquityValue);
    }

    if (isEquityValueFocused) {
      setIsEquityValueFocused(false);
    }
  };

  const handleSubmit = async ({ priceItem }: PublicOfferFormDataT3) => {
    const priceItemUnformatted = Utils.Unmask.Money(priceItem);
    tableForm.setValue('priceItem', priceItem);
    setValueApplied(priceItemUnformatted);

    setIsValueFocused(false);
  };

  const minMaxValueDescription = useMemo(() => getEquityValueDescription(offer), [offer]);

  return {
    minimumValue,
    minimumEquityValue,
    handleInputValueFocus,
    handleInputEquityValueFocus,
    handleInputEquityValueClear,
    handleInputValueClear,
    isValueFocused,
    handleInputValueBlur,
    handleInputEquityValueBlur,
    isEquityValueFocused,
    handleSubmit: tableForm.handleSubmit(handleSubmit),
    control: tableForm.control as unknown as Control,
    hasErrorValue: tableForm.formState.errors?.priceItem,
    hasErrorEquityValue: tableForm.formState.errors?.equityValue,
    currentValue: valueApplied,
    currentValueFormatted: valueAppliedFormatted,
    currentEquityValue: equityValueApplied,
    currentEquityValueFormatted: equityValueAppliedFormatted,
    minMaxValueDescription,
  };
};

export type PublicOfferEquityController = ReturnType<typeof usePublicOfferAssetEquityController>;
